import React from "react";
import { ShieldCheck, FileText } from "lucide-react";
import { getLegalStatementForInvoice } from "@/utils/legalStatements";

/**
 * LegalStatementPreview — zeigt FL beim Ausfüllen einer Rechnung den
 * exakten Pflichthinweis-Text 1:1 an, der am Ende auf dem PDF erscheinen wird.
 *
 * Compliance-Hintergrund (User-Auftrag Feb 2026):
 * Der FL soll vor dem Versand sehen können, welcher gesetzliche Pflichthinweis
 * automatisch auf seine Rechnung gedruckt wird (KU-Hinweis, RC, IGL, Export, …).
 * Die Texte hier kommen aus `utils/legalStatements.js` und sind durch einen
 * Backend-Test (`test_legal_statement_compliance.py`) gegen das PDF-Renderer-
 * Modul `statements.py` abgesichert — 100% Identität garantiert.
 *
 * Props:
 *   country - "DE" | "AT" | "CH"
 *   scenario - "standard" | "reverse_charge" | "intra_community" | "export" | "small_business" | "exempt"
 *   isKleinunternehmer - true bei aktivem KU-Status
 *   variant - "card" (default, gerahmt) | "inline" (subtiler, inline)
 */
export default function LegalStatementPreview({
  country,
  scenario,
  isKleinunternehmer,
  variant = "card",
}) {
  const stmt = getLegalStatementForInvoice({
    country,
    scenario,
    isKleinunternehmer,
  });

  if (!stmt) return null;

  if (variant === "inline") {
    return (
      <div
        className="text-xs text-emerald-900 bg-emerald-50/70 border border-emerald-200 rounded-md px-3 py-2 flex items-start gap-2"
        data-testid="legal-statement-preview-inline"
      >
        <ShieldCheck className="w-3.5 h-3.5 mt-0.5 text-emerald-600 flex-shrink-0" />
        <div>
          <div className="font-medium leading-snug">{stmt.text}</div>
          <div className="text-[10px] text-emerald-700 mt-0.5">
            Pflichthinweis — wird automatisch auf der PDF-Rechnung ausgewiesen.
          </div>
        </div>
      </div>
    );
  }

  // Card variant — prominent
  return (
    <div
      className="border-2 border-emerald-300 bg-emerald-50/60 rounded-xl p-4"
      data-testid="legal-statement-preview-card"
    >
      <div className="flex items-start gap-3">
        <div className="bg-emerald-100 rounded-lg p-2 flex-shrink-0">
          <FileText className="w-4 h-4 text-emerald-700" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <div className="text-sm font-semibold text-emerald-900">
              Pflichthinweis auf der PDF-Rechnung
            </div>
            <span
              className="text-[10px] bg-emerald-200 text-emerald-900 px-1.5 py-0.5 rounded font-medium"
              title="Identisch zum finalen PDF-Text"
            >
              1:1 wie im PDF
            </span>
          </div>
          <div
            className="text-sm text-slate-900 font-medium bg-white border border-emerald-200 rounded-md px-3 py-2 leading-relaxed"
            data-testid="legal-statement-preview-text"
          >
            „{stmt.text}"
          </div>
          {stmt.legalRef && (
            <div className="text-[11px] text-emerald-800 mt-2 flex items-center gap-1">
              <ShieldCheck className="w-3 h-3" />
              Rechtsgrundlage: <strong>{stmt.legalRef}</strong>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
